// ENG-4219 / ENG-3775 — the shared bridge-unreachable terminal-state UI.
//
// Product decision (ENG-3775, 2026-08-25): when a bounded retry loop runs out
// of attempts, the user must see a terminal state — "We couldn't connect to
// your bridge" — WITH a Retry button, reported to PostHog as a HANDLED event.
// ENG-3775's broker bootstrap/reconnect call sites must REUSE this exact UI
// (same boot surface), so the copy and API here stay deliberately generic:
// one message, one Retry action, keyed by caller — and explicitly NO
// cause-specific variants (offline vs. bridge down vs. auth), per the same
// decision. Per rule: think-in-systems (ai.knowledge/global) — one reusable
// module, callers only supply how to re-arm their own loop.

import { Button } from '@studio/design-system/button';
import type { ReactElement } from 'react';
import { hide, post } from '#~/notifications/index.js';

/**
 * The one approved terminal-state copy (verbatim from the ENG-3775 decision).
 * Callers must not fork this per failure cause.
 */
export const BRIDGE_UNREACHABLE_MESSAGE = "We couldn't connect to your bridge.";

/** Notification title — generic on purpose (no cause-specific variants). */
export const BRIDGE_UNREACHABLE_TITLE = 'Unable to Connect';

/**
 * Stable notification id per caller key (agent id, broker session, …) so a
 * repeat exhaustion replaces the standing notification instead of stacking,
 * and the Retry handler can dismiss exactly its own notification.
 */
export const bridgeUnreachableNotificationId = (key: string): string => `bridge-unreachable-${key}`;

/**
 * The terminal-state body: the approved message plus a Retry button.
 * Exported separately from the poster so ENG-3775 call sites that render
 * inside their own surface (not the notification stack) can reuse it.
 */
export const BridgeUnreachableMessage = ({ onRetry }: { onRetry: () => void }): ReactElement => (
  <div>
    <span className='text-[12px]'>{BRIDGE_UNREACHABLE_MESSAGE}</span>
    <div className='mb-0 flex flex-row gap-8'>
      {/* Mirrors the watchdog update-prompt button styling so the two
          notification CTAs on this boot surface look identical. The
          focus-visible ring is added because the link variant has no focus
          ring of its own and the underline is removed — without it a
          keyboard user tabbing to Retry gets no visible focus indication
          (same pattern as the restore-failure notice's Retry CTA). */}
      <Button
        variant='link'
        className='focus-visible:ring-primary/50 px-0 pt-12 text-primary no-underline focus-visible:ring-2'
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  </div>
);

/**
 * Post the terminal-state notification. `autoClose: false` — a terminal
 * failure must not silently vanish before the user can act on it (the whole
 * point of ENG-4219 was replacing an unbounded spinner with a DEFINITE,
 * actionable state). Retry dismisses the notification first, then re-arms
 * the caller's loop, so a fresh exhaustion posts a fresh notification.
 *
 * @returns the notification id (for callers that want to hide it early,
 * e.g. when the bridge recovers through another channel).
 */
export const postBridgeUnreachable = ({
  key,
  onRetry
}: {
  key: string;
  onRetry: () => void;
}): string => {
  const id = bridgeUnreachableNotificationId(key);

  post(
    {
      // Per F10 (ENG-4721) / A8: this terminal is the one class of failure the
      // masthead bell exists for. `actionable: true` lets unreadActionableCount
      // light the bell — the Retry below is the only recovery affordance, so a
      // dark bell would hide it. Allow-listed in actionable-allow-list.txt.
      actionable: true,
      id,
      message: (
        <BridgeUnreachableMessage
          onRetry={() => {
            hide(id);
            onRetry();
          }}
        />
      ),
      severity: 'error',
      title: BRIDGE_UNREACHABLE_TITLE
    },
    { autoClose: false }
  );

  return id;
};
